import React, { useState, useEffect } from 'react';
import { Tree, Input, Spin, Empty, Button } from 'antd';
import { FolderOutlined, FileOutlined, CheckSquareOutlined } from '@ant-design/icons';
import { testItemService } from '../../services/testItemService';
import { TestItem, Folder, TestItemType } from '../../types/testItem';
import type { DataNode } from 'antd/es/tree';

interface TestItemSelectorProps {
    projectId: number;
    onSelectionChange: (selectedIds: number[]) => void;
    initialSelection?: number[];
}

interface TreeNode extends DataNode {
    data: TestItem | Folder;
}

export const TestItemSelector: React.FC<TestItemSelectorProps> = ({
    projectId,
    onSelectionChange,
    initialSelection = []
}) => {
    const [loading, setLoading] = useState(true);
    const [treeData, setTreeData] = useState<TreeNode[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectedKeys, setSelectedKeys] = useState<string[]>(
        initialSelection.map(id => `item-${id}`)
    );
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchData();
    }, [projectId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch folders
            const folders = await testItemService.getFolders(projectId);
            console.log('Fetched folders:', folders);

            // Fetch both test cases and checklists
            const [testCases, checklists] = await Promise.all([
                testItemService.getTestItems({ project_id: projectId, type: TestItemType.TEST_CASE }),
                testItemService.getTestItems({ project_id: projectId, type: TestItemType.CHECKLIST })
            ]);

            console.log('Fetched test cases:', testCases);
            console.log('Fetched checklists:', checklists);

            // Combine test cases and checklists
            const testItems = [...testCases, ...checklists];

            const treeNodes = buildTreeData(folders, testItems);
            setTreeData(treeNodes);
            setExpandedKeys(treeNodes.map(node => node.key));
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load test items. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getItemIcon = (item: TestItem) => {
        switch (item.type) {
            case TestItemType.TEST_CASE:
                return <FileOutlined />;
            case TestItemType.CHECKLIST:
                return <CheckSquareOutlined />;
            default:
                return <FileOutlined />;
        }
    };

    const buildTreeData = (folders: Folder[], testItems: TestItem[]): TreeNode[] => {
        const folderMap = new Map<number, Folder>();
        folders.forEach(folder => folderMap.set(folder.id, folder));

        const buildFolderNode = (folder: Folder): TreeNode => {
            const childFolders = folders.filter(f => f.parent_id === folder.id);
            const childItems = testItems.filter(item => item.folder_id === folder.id);

            return {
                key: `folder-${folder.id}`,
                title: folder.name,
                icon: <FolderOutlined />,
                children: [
                    ...childFolders.map(buildFolderNode),
                    ...childItems.map(item => ({
                        key: `item-${item.id}`,
                        title: item.name,
                        icon: getItemIcon(item),
                        isLeaf: true,
                        data: item
                    }))
                ],
                data: folder
            };
        };

        // Get root folders
        const rootFolders = folders.filter(f => !f.parent_id);
        
        // Get items that are not in any folder
        const rootItems = testItems.filter(item => !item.folder_id);

        // Create root level nodes
        const rootNodes = [
            ...rootFolders.map(buildFolderNode),
            ...rootItems.map(item => ({
                key: `item-${item.id}`,
                title: item.name,
                icon: getItemIcon(item),
                isLeaf: true,
                data: item
            }))
        ];

        return rootNodes;
    };

    const onSearch = (value: string) => {
        setSearchValue(value);
    };

    const filterTreeData = (data: DataNode[]): DataNode[] => {
        return data.map(node => {
            const title = node.title as string;
            const match = title.toLowerCase().includes(searchValue.toLowerCase());

            if (node.children) {
                const filteredChildren = filterTreeData(node.children);
                if (filteredChildren.length > 0 || match) {
                    return {
                        ...node,
                        children: filteredChildren
                    };
                }
            }

            return match ? node : null;
        }).filter(Boolean) as DataNode[];
    };

    if (error) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <p style={{ color: 'red' }}>{error}</p>
                <Button onClick={fetchData}>Retry</Button>
            </div>
        );
    }

    return (
        <div>
            <Input.Search
                placeholder="Search test items..."
                onChange={e => onSearch(e.target.value)}
                style={{ marginBottom: 8 }}
            />
            <Spin spinning={loading}>
                {treeData.length > 0 ? (
                    <Tree
                        showIcon
                        showLine
                        expandedKeys={expandedKeys}
                        onExpand={setExpandedKeys}
                        treeData={searchValue ? filterTreeData(treeData) : treeData}
                        selectable={false}
                        checkable={true}
                        checkedKeys={selectedKeys}
                        onCheck={(checkedKeys) => {
                            const newSelection = Array.isArray(checkedKeys) 
                                ? checkedKeys 
                                : checkedKeys.checked;
                            setSelectedKeys(newSelection as string[]);
                            onSelectionChange(
                                (newSelection as string[])
                                    .filter(key => key.startsWith('item-'))
                                    .map(key => parseInt(key.split('-')[1]))
                            );
                        }}
                    />
                ) : (
                    <Empty description="No test items found" />
                )}
            </Spin>
        </div>
    );
}; 